/**
 * Install Button Component - Manual PWA install trigger
 */

import { useCallback } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import './InstallButton.css';

interface InstallButtonProps {
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  onInstall?: () => void;
  onInstallStart?: () => void;
  onInstallFail?: () => void;
}

export function InstallButton({
  variant = 'secondary',
  size = 'md',
  onInstall,
  onInstallStart,
  onInstallFail,
}: InstallButtonProps) {
  const { isInstallable, isInstalling, isInstalled, install } = usePWAInstall();

  const handleInstall = useCallback(async () => {
    if (!isInstallable || isInstalling) return;

    onInstallStart?.();
    const success = await install();

    if (success) {
      onInstall?.();
    } else {
      onInstallFail?.();
    }
  }, [isInstallable, isInstalling, install, onInstall, onInstallStart, onInstallFail]);

  // Don't render if app is already installed or not installable
  if (isInstalled || !isInstallable) {
    return null;
  }

  return (
    <button
      type="button"
      className={`install-button install-button--${variant} install-button--${size}`}
      onClick={handleInstall}
      disabled={isInstalling}
      title="Install Refinevoice as an app"
    >
      <span className="install-button-icon">{isInstalling ? '⏳' : '📱'}</span>
      <span className="install-button-text">{isInstalling ? 'Installing...' : 'Install App'}</span>
    </button>
  );
}
